/**
 * Smart routing utility for ThreadLock
 * 
 * Determines the best dashboard route based on user privilege level:
 * Priority: Admin > Pro > User
 * 
 * This ensures ThreadLock employees (admins) who also have pro access
 * are routed to the admin dashboard by default, providing them with
 * the highest level of access and functionality.
 */

import type { ParsedToken } from 'firebase/auth';
import { hasProClaim } from './claimsUtils';

export interface DashboardRoute {
  path: string;
  priority: 'admin' | 'pro' | 'user';
  label: string;
}

/**
 * Get the default dashboard route based on user claims.
 * Reads from already-resolved AuthProvider state — no SDK calls.
 *
 * @param claims - Parsed ID token claims from AuthProvider state
 * @returns The path to redirect to
 */
export function getDefaultDashboard(claims: ParsedToken | null | undefined): string {
  if (!claims) return '/cases';

  // Admin has highest priority
  if (claims.admin === true) return '/admin';

  // Pro has second priority — check all three claim variants set by the backend
  if (hasProClaim(claims)) return '/pro/dashboard';

  // Default: SRL (Self-Represented Litigant) workspace
  return '/cases';
}

/**
 * Get all available dashboards for a user
 * Useful for showing navigation options
 *
 * @param claims - Parsed ID token claims from AuthProvider state
 * @returns Array of available dashboard routes
 */
export function getAvailableDashboards(
  claims: ParsedToken | null | undefined
): DashboardRoute[] {
  const dashboards: DashboardRoute[] = [];

  // Everyone gets the user dashboard
  dashboards.push({
    path: '/dashboard',
    priority: 'user',
    label: 'My Cases'
  });

  if (claims) {
    // Pro users get the pro dashboard
    if (hasProClaim(claims)) {
      dashboards.push({
        path: '/pro/dashboard',
        priority: 'pro',
        label: 'Professional Portal'
      });
    }

    // Admins get the admin dashboard
    if (claims.admin === true) {
      dashboards.push({
        path: '/admin',
        priority: 'admin',
        label: 'Admin Dashboard'
      });
    }
  }

  // Sort by priority (admin > pro > user)
  return dashboards.sort((a, b) => {
    const priorityOrder = { admin: 3, pro: 2, user: 1 };
    return priorityOrder[b.priority] - priorityOrder[a.priority];
  });
}

/**
 * Check if user has access to a specific privilege level
 *
 * @param claims - Parsed ID token claims from AuthProvider state
 * @param level - The privilege level to check
 * @returns True if user has the specified privilege
 */
export function hasPrivilege(
  claims: ParsedToken | null | undefined,
  level: 'admin' | 'pro' | 'user'
): boolean {
  switch (level) {
    case 'admin':
      return !!(claims?.admin === true);
    case 'pro':
      return hasProClaim(claims);
    case 'user':
      return true; // All authenticated users are "users"
    default:
      return false;
  }
}

